import { formatCurrency } from '@eriko/shared-types';
import { Users } from 'lucide-react';

interface Contractor {
  vendorId: string;
  name: string;
  tin: string | null;
  totalPaid: number;
  paymentCount: number;
  exceedsThreshold: boolean;
}

interface Data {
  type: string;
  year: number;
  threshold: number;
  contractors: Contractor[];
  totalPaid: number;
  exceedsThresholdCount: number;
  belowThresholdCount: number;
}

function StatCell({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="px-4.5 py-3 text-center">
      <div className="text-[11px] font-medium uppercase tracking-[0.06em] text-surface-500">
        {label}
      </div>
      <div className="mt-1 font-mono text-sm font-semibold tabular-nums text-surface-900">
        {value}
      </div>
    </div>
  );
}

export function Contractor1099Report({ data }: { data: Record<string, unknown> }) {
  const d = data as unknown as Data;
  const maskTin = (t: string | null) => (t ? `•••-••-${t.slice(-4)}` : '—');
  const isEmpty = d.contractors.length === 0;

  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-surface-100 px-4.5 py-3.5">
        <div className="flex items-center gap-2.5">
          <Users className="h-4.25 w-4.25 text-surface-400" strokeWidth={1.5} />
          <span className="text-sm font-semibold text-surface-900">1099 Contractor Summary</span>
        </div>
        <span className="rounded-full bg-surface-50 px-2.5 py-0.75 text-xs font-medium text-surface-500">
          Tax year {d.year}
        </span>
      </div>

      {/* Summary stat strip */}
      <div className="grid grid-cols-3 divide-x divide-surface-100 border-b border-surface-100">
        <StatCell label="Above threshold" value={d.exceedsThresholdCount} />
        <StatCell label="Total paid" value={formatCurrency(d.totalPaid, 2)} />
        <StatCell label="Contractors" value={d.contractors.length} />
      </div>

      {/* Table */}
      {isEmpty ? (
        <div className="px-4.5 py-10 text-center text-[13px] text-surface-500">
          No contractor payments recorded for this year.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-50/60 text-[11px] uppercase tracking-[0.06em] text-surface-500">
                <th className="px-4.5 py-2 text-left font-medium">Contractor</th>
                <th className="px-3 py-2 text-left font-medium">TIN</th>
                <th className="px-3 py-2 text-right font-medium">Payments</th>
                <th className="px-3 py-2 text-right font-medium">Total paid</th>
                <th className="px-4.5 py-2 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {d.contractors.map((c) => (
                <tr
                  key={c.vendorId}
                  className="group border-t border-surface-100 transition-colors hover:bg-surface-50"
                >
                  <td className="px-4.5 py-1.75 text-[13px] text-surface-700">{c.name}</td>
                  <td className="px-3 py-1.75 font-mono text-[12px] tabular-nums text-surface-500">
                    {maskTin(c.tin)}
                  </td>
                  <td className="px-3 py-1.75 text-right font-mono text-[13px] tabular-nums text-surface-500">
                    {c.paymentCount}
                  </td>
                  <td className="px-3 py-1.75 text-right font-mono text-[13px] font-semibold tabular-nums text-surface-900">
                    {formatCurrency(c.totalPaid, 2)}
                  </td>
                  <td className="px-4.5 py-1.75">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        c.exceedsThreshold
                          ? 'bg-warn-surface text-warn'
                          : 'bg-surface-50 text-surface-500'
                      }`}
                    >
                      {c.exceedsThreshold ? '1099 required' : 'Below threshold'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
